import { useMemo } from 'react'
import { parseAmount } from '../../utils/currency'

export interface CalculatorInput {
  price: string
  down: string
  tradeValue: string
  owed: string
  apr: string
  term: number
}

export interface LoanEstimate {
  /** tradeValue - owed. Negative means the visitor is upside down. */
  equity: number
  isUpsideDown: boolean
  amountFinanced: number
  monthlyPayment: number
  hasEnoughInput: boolean
}

/**
 * Standard amortization on numbers the VISITOR enters. Nothing is pre-filled and
 * no rate, price, term, or payment is suggested by the page.
 */
export function useLoanEstimate(input: CalculatorInput): LoanEstimate {
  return useMemo(() => {
    const price = parseAmount(input.price)
    const down = parseAmount(input.down)
    const tradeValue = parseAmount(input.tradeValue)
    const owed = parseAmount(input.owed)
    const apr = parseAmount(input.apr)

    const equity = tradeValue - owed
    // Negative equity rolls into the amount financed, which is what actually happens.
    const amountFinanced = Math.max(price - down - equity, 0)

    const monthlyRate = apr / 100 / 12
    const term = input.term

    let monthlyPayment = 0
    if (amountFinanced > 0 && term > 0) {
      monthlyPayment =
        monthlyRate === 0
          ? amountFinanced / term
          : (amountFinanced * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -term))
    }

    return {
      equity,
      isUpsideDown: equity < 0,
      amountFinanced,
      monthlyPayment,
      hasEnoughInput: price > 0,
    }
  }, [input.price, input.down, input.tradeValue, input.owed, input.apr, input.term])
}
